import React from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { pxToRem } from '@withjoy/joykit/theme';
import { productGridStyles } from './ExternalProductGrid.styles';
import { useTranslation } from '@shared/core';
import { ExternalProductTile, ExternalProductTileProps } from '../ExternalProductTile';
import { ExternalProduct } from '../../../../Catalog.types';

const ProductGridLayout: React.FC = ({ children }) => {
  return <Box __css={productGridStyles}>{children}</Box>;
};

type SearchResultsGridProps = {
  loading: boolean;
  products: ExternalProduct[] | null;
  renderProduct: (props: ExternalProductTileProps) => JSX.Element;
  hideHeader: boolean;
  isMobile: boolean;
};

type SearchResultsGridRootProps = {
  products: ExternalProduct[];
  renderProduct: (props: ExternalProductTileProps) => JSX.Element;
  isMobile: boolean;
};

const Root = ({ products, renderProduct, isMobile }: SearchResultsGridRootProps) => {
  return (
    <ProductGridLayout>
      {products.map((product: ExternalProduct, index: number) =>
        renderProduct({
          index,
          isMobile,
          product
        })
      )}
    </ProductGridLayout>
  );
};

const SKELETON_ARRAY = Array.from(Array(8));

const Skeleton = () => {
  return (
    <ProductGridLayout>
      {SKELETON_ARRAY.map((_, idx) => (
        <ExternalProductTile.Skeleton key={idx} />
      ))}
    </ProductGridLayout>
  );
};

const Header = () => {
  const { t } = useTranslation('catalogRegistry');

  const trans = t('universalSearch', 'productgrid');

  return (
    <Flex alignItems="center" flexDirection="column" rowGap={5}>
      <TextV2 typographyVariant="Hed56">{trans.sectionHed()}</TextV2>
      <TextV2 typographyVariant="hed4">{trans.sectionDek()}</TextV2>
    </Flex>
  );
};

export const ExternalProductsGrid = (props: SearchResultsGridProps) => {
  const { products, hideHeader, loading, isMobile, renderProduct } = props;

  if (!loading && !products?.length) {
    return null;
  }

  return (
    <Flex flexDirection="column">
      {!hideHeader && <Header />}
      <SkeletonGroup isReady={!loading} marginBottom={pxToRem(131)} placeholder={<Skeleton />}>
        <Box display="flex" flexDirection="column">
          {products && <Root products={products} isMobile={isMobile} renderProduct={renderProduct} />}
        </Box>
      </SkeletonGroup>
    </Flex>
  );
};
